<template>
    <div class="default-from-address">
        <h1>
            {{ $t('settings.email.defaultFromAddress.defaultFromAddress') }}
        </h1>

        <p class="description">
            {{ $t('settings.email.defaultFromAddress.defaultFromAddressDescription') }}

            <ds-link
                :href="learnMoreLink"
                external
            >
                {{ $t('settings.email.defaultFromAddress.learnMore') }}
            </ds-link>
        </p>

        <DsPlaceholder
            class="placeholder"
            :loading="loading"
            :rows="placeholderRows"
        />

        <form
            v-if="!loading"
            ref="form"
            novalidate
            @submit.prevent="submit"
        >
            <div class="form-container">
                <div class="domain-input-container">
                    <DsInputField
                        v-model="username"
                        class="input-fields"
                        :placeholder="$t('settings.email.defaultFromAddress.emailUsername')"
                        :label="$t('settings.email.defaultFromAddress.emailUsername')"
                        data-qa="email-username"
                        required
                        :invalid="usernameInvalid && submitted"
                        :submitted="submitted"
                        :readonly="saving"
                        @input="savedFromAddressUnchanged = false"
                    />

                    <DsIcon name="at-sign" size="sm" class="at-sign" />

                    <DsMultiselect
                        v-model="emailDomain"
                        class="input-fields"
                        :placeholder="$t('settings.email.defaultFromAddress.emailDomain')"
                        required
                        allow-add
                        :add-text="$t('settings.email.defaultFromAddress.connectDomain')"
                        :options="verifiedEmailDomainOptions"
                        :force-invalid="domainInvalid && submitted"
                        :readonly="saving"
                        data-qa="email-domain"
                        @add="connectDomain"
                        @update:modelValue="savedFromAddressUnchanged = false"
                    />
                </div>

                <DsInlineAlert
                    v-if="loadError"
                    leading-icon
                    type="warning"
                    class="load-error-alert"
                    data-qa="load-error-alert"
                >
                    {{ $t('settings.email.defaultFromAddress.loadError') }}
                </DsInlineAlert>

                <DsFilledButton
                    class="save-changes-button"
                    type="submit"
                    :disabled="savedFromAddressUnchanged"
                    :aria-label="$t('settings.email.defaultFromAddress.saveChanges')"
                    :loading="saving"
                >
                    {{ $t("settings.email.defaultFromAddress.saveChanges") }}
                </DsFilledButton>

                <DsInlineAlert
                    v-if="saveError"
                    leading-icon
                    type="critical"
                    class="save-error-alert"
                    data-qa="save-error-alert"
                >
                    {{ $t('settings.email.defaultFromAddress.saveError') }}
                </DsInlineAlert>
            </div>
        </form>
    </div>
</template>

<script>
import { getVerifiedDomainsNewKeys } from '@/components/settings/util/domains.util';
import { getCustomDomains } from '@/api/broadcasts/emailBroadcast';
import axios from 'axios';

export default {
    emits: ['connect-domain'],

    props: {
        appId: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            username: null,
            emailDomain: null,
            usernameInvalid: false,
            domainInvalid: false,
            submitted: false,
            saveError: false,
            loadError: false,
            savedFromAddressUnchanged: false,
            loading: false,
            saving: false,
            placeholderRows: [
                { height: '3rem', boxes: [1, 1] },
                { height: '3rem', boxes: [1] },
            ],
            verifiedDomains: null,
            learnMoreLink: 'https://help.keap.com/help/fallback-from-address-for-email-sending',
        };
    },

    mounted() {
        this.loadDefaultFromAddress();
    },

    computed: {
        verifiedEmailDomainOptions() {
            return this.verifiedDomains?.map((domain) => ({
                label: domain,
                value: domain,
            }));
        },

        isAddressInvalid() {
            return this.usernameInvalid || this.domainInvalid;
        },

        defaultFromAddress() {
            return `${this.username}@${this.emailDomain?.value}`;
        },
    },

    methods: {
        async loadDefaultFromAddress() {
            this.loading = true;

            try {
                let defaultFromAddress = null;

                try {
                    const { data: { defaultFromAddress: address } } = await axios
                        .get('/app/tenantSettings/defaultFromAddress');

                    defaultFromAddress = address;
                } catch (error) {
                    this.loadError = true;
                }

                const customDomains = await getCustomDomains();

                this.verifiedDomains = getVerifiedDomainsNewKeys(customDomains);

                if (defaultFromAddress) {
                    const defaultFromAddressSplit = defaultFromAddress.split('@');

                    const domain = defaultFromAddressSplit[1];

                    if (!this.verifiedEmailDomainOptions.some((domainOption) => domainOption.value === domain)) {
                        // TODO: show an error that the domain is not found in the verified domains.
                    }

                    this.emailDomain = {
                        label: domain,
                        value: domain,
                    };

                    [this.username] = defaultFromAddressSplit;
                }

                if (!this.isAddressInvalid) {
                    this.savedFromAddressUnchanged = true;
                }
            } catch (error) {
                this.loadError = true;
            }

            this.loading = false;
        },

        validateAddress() {
            if (!this.username) {
                this.usernameInvalid = true;
            } else {
                this.usernameInvalid = false;
            }

            if (!this.emailDomain || !this.emailDomain.value) {
                this.domainInvalid = true;
            } else {
                this.domainInvalid = false;
            }
        },

        async submit() {
            this.saveError = false;
            this.submitted = true;
            this.validateAddress();

            if (this.isAddressInvalid) {
                return;
            }

            this.saving = true;

            try {
                let defaultFromAddress = null;

                try {
                    const { data: { defaultFromAddress: address } } = await axios
                        .post('/app/tenantSettings/defaultFromAddress', {
                            defaultFromAddress: this.defaultFromAddress,
                        });

                    defaultFromAddress = address;
                } catch (error) {
                    this.saveError = true;
                }

                if (defaultFromAddress) {
                    this.submitted = false;
                    this.$toast({
                        message: this.$t('settings.email.defaultFromAddress.saveSuccess'),
                    });

                    this.savedFromAddressUnchanged = true;
                } else {
                    throw new Error('defaultFromAddress was unexpectedly null after save');
                }
            } catch (error) {
                this.saveError = true;
                this.savedFromAddressUnchanged = false;
            }

            this.saving = false;
        },

        connectDomain() {
            this.$emit('connect-domain');
        },
    },
};
</script>

<style lang="scss" scoped>
.default-from-address {
    margin-top: $spacing-200;
}

.domain-input-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
}

.description {
    margin-top: $spacing-200;
    width: px-to-rem(500px);
}

.input-fields {
    width: px-to-rem(266px);
}

.at-sign {
    margin-left: $spacing-100;
    margin-right: $spacing-100;
    margin-bottom:  $spacing-300;
}

.save-changes-button {
    width: px-to-rem(130px);
    color: white;
}

.save-error-alert {
    margin-top: $spacing-300;
    margin-right: auto;
}

.load-error-alert {
    margin-bottom: $spacing-300;
    width: px-to-rem(564px);
}

.form-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.placeholder {
    width: px-to-rem(564px);
}

</style>

<i18n>
{
    "en-us": {
        "defaultFromAddress": "Default From address",
        "defaultFromAddressDescription": "Add a default From address to make sure your emails are always delivered. For example, if a broadcast or automation you’ve set up is sent from an unverified address, it will use the below default From email to ensure proper delivery.",
        "emailUsername": "Email username",
        "emailDomain": "Email domain",
        "saveChanges": "Save changes",
        "connectDomain": "Connect email domain",
        "loadError": "Your default From address can’t be loaded at this time. If you make any changes, it will overwrite what is currently saved.",
        "saveError": "Something went wrong while saving your default From address. Try again.",
        "saveSuccess": "Default From address saved."
    }
}
</i18n>
